import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import { PermissionButton } from "@/components/ui/PermissionButton";
import PrintButton from "@/components/ui/PrintButton";
import ExcelButton from "@/components/ui/ExcelButton";
import ButtonGroup from "@/components/ui/ButtonGroup";
import DateBox from "@/components/ui/DateBox";
import DateSearchBtn from "@/components/ui/DateSearchBtn";
import { InputBox } from "@/components/ui/InputBox";
import { CheckBox } from "@/components/ui/CheckBox";
import SelectBox from "@/components/ui/SelectBox";
import CountBox from "@/components/ui/CountBox";
import { useState, useMemo, useEffect, useRef } from "react";
import Table, { TableColumn } from "@/components/ui/Table";
import { ContextMenuItem } from "@/components/ui/ContextMenu";
import { Footer, FooterCell } from "@/components/layout/Footer";
import Add from "@/components/modal/dispatch/order/Add";
import PerformanceModal from "@/components/modal/performance/PerformanceModal";
import DriverSearchModal from "@/components/modal/performance/DriverSearchModal";
import {
  useOrderSlipReport,
  useDeleteOrderSlips,
  useUploadOrderSlipExcel,
  orderSlipQueryKey,
  downloadOrderSlipUploadTemplate,
} from "@/hooks/dispatch/dp_ordeer_slip/useOrderSlip";
import { useCodeListByCompanyCode, useInfiniteScrollPage } from "@/hooks";
import { api } from "@/lib/axios";
import { getToday, getLocalISOString, getDateOffset } from "@/lib/date";
import { SaleSlip } from "@/interface";
import { toast } from "sonner";
import { ReportData } from "@/interface";
import { DriverCode } from "@/interface/standard/st_driver_code";
import { useInsertAndUpdateDriverOrder, useDeleteDriverOrder } from "@/hooks/dispatch/driver_order/useDriverOrder";
import { useUserInfo } from "@/hooks/member/useMember";
import { useQueryClient } from "@tanstack/react-query";

export default function OrderPage() {
  const queryClient = useQueryClient();
  // React Query 훅으로 회사 데이터 가져오기
  const { data: companyCodeList = [] } = useCodeListByCompanyCode();
  // 사용자 정보 가져오기
  const { data: userInfo } = useUserInfo();

  // 삭제 mutation
  const deleteOrderSlipsMutation = useDeleteOrderSlips();
  // 배차요청 mutation
  const insertDriverOrderMutation = useInsertAndUpdateDriverOrder();
  // 배차취소 mutation
  const deleteDriverOrderMutation = useDeleteDriverOrder();
  // Excel 업로드 mutation
  const uploadExcelMutation = useUploadOrderSlipExcel();

  // Excel 업로드용 file input ref
  const excelFileInputRef = useRef<HTMLInputElement>(null);

  // 입력용 state (UI에서 사용)
  const [searchValue, setSearchValue] = useState("");

  // 검색 필드 선택 및 검색어
  const [searchField, setSearchField] = useState("custName"); // 기본값: 거래처
  const [searchKeyword, setSearchKeyword] = useState(""); // 검색어 입력값
  const [searchField2, setSearchField2] = useState("carNumber"); // 두 번째 검색 필드 기본값: 차량번호
  const [searchKeyword2, setSearchKeyword2] = useState(""); // 두 번째 검색어 입력값

  // 정확히 일치 검색 여부
  const [exactMatch, setExactMatch] = useState(false);
  const [actualExactMatch, setActualExactMatch] = useState(false);

  // 실제 적용된 검색 조건 (조회 버튼 클릭 시 적용)
  const [appliedSearchDto, setAppliedSearchDto] = useState<{
    companyCode?: string;
    custName?: string;
    carNumber?: string;
    driverName?: string;
    driverTel?: string;
    inLocation?: string;
    outLocation?: string;
    outArea1?: string;
    outArea2?: string;
    dispatchStaffName?: string;
    itemName?: string;
    delYn?: string;
    exactMatch?: boolean;
  }>({ delYn: "N" });

  // 익일 날짜를 기본값으로 설정
  const tomorrowStr = getDateOffset(1);

  const [startDate, setStartDate] = useState(tomorrowStr);
  const [endDate, setEndDate] = useState(tomorrowStr);

  const [selectedValue, setSelectedValue] = useState("all");
  const [selectedValue2, setSelectedValue2] = useState("all2");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isPerformanceModalOpen, setIsPerformanceModalOpen] = useState(false);
  const [performanceModalData, setPerformanceModalData] = useState<ReportData | null>(null);
  const [isDriverSearchModalOpen, setIsDriverSearchModalOpen] = useState(false);
  const [selectedDriverCode, setSelectedDriverCode] = useState<string | null>(null);
  const [isLoadingSaleSlip, setIsLoadingSaleSlip] = useState(false);

  // 매출정보 조회 함수 (직접 API 호출 - 캐싱 문제 해결)
  const fetchSaleSlipByOrderCode = async (orderCode: string) => {
    setIsLoadingSaleSlip(true);
    try {
      const saleSlip: SaleSlip = await api.get("/sale-slip/order-code", { params: { orderCode } });

      if (saleSlip) {
        // SaleSlip을 ReportData로 변환
        const reportData: ReportData = {
          reportType: saleSlip.saleSlipCompanyGb || "1",
          contractUniqueNo: saleSlip.saleSlipOrderSlipCode || "",
          custName: "",
          custBizNumber: "",
          contractDate: saleSlip.saleSlipDate ? `${saleSlip.saleSlipDate}T00:00:00` : getLocalISOString(),
          contractAmount: saleSlip.saleSlipSaleTotalAmt || 0,
          itemGroupCode: "",
          otherTransportUse: "",
          pickupBranch: saleSlip.saleSlipInBranch || "",
          pickupLocation: saleSlip.saleSlipInLocation || "",
          pickupQty: saleSlip.saleSlipInQty || 0,
          dropoffLocation: saleSlip.saleSlipOutLocation || "",
          dropoffArea1: saleSlip.saleSlipOutArea1 || "",
          dropoffArea2: saleSlip.saleSlipOutArea2 || "",
          dropoffArea3: saleSlip.saleSlipOutArea3 || null,
          dropoffQty1: saleSlip.saleSlipOutQty1 || 0,
          dropoffQty2: saleSlip.saleSlipOutQty2 || 0,
          dropoffQty3: saleSlip.saleSlipOutQty3 || 0,
          vehicleNumber: "",
          transportDoneDate: saleSlip.saleSlipDate ? `${saleSlip.saleSlipDate}T00:00:00` : getLocalISOString(),
          transportFee: saleSlip.saleSlipSaleMoveAmt || 0,
          saleUnitPrice: saleSlip.saleSlipSalePrice || 0,
          saleAmount: saleSlip.saleSlipSaleSupportAmt || 0,
          saleMoveFee: saleSlip.saleSlipSaleMoveAmt || 0,
          saleVat: saleSlip.saleSlipSaleSurtaxAmt || 0,
          saleTotal: saleSlip.saleSlipSaleTotalAmt || 0,
          buyUnitPrice: saleSlip.saleSlipBuyPrice || 0,
          buyAmount: saleSlip.saleSlipBuySupportAmt || 0,
          buyMoveFee: saleSlip.saleSlipBuyMoveAmt || 0,
          buyLaborFee: saleSlip.saleSlipBuyLaborAmt || 0,
          buyMultiFee: saleSlip.saleSlipBuyMultyAmt || 0,
          buyWeightFee: saleSlip.saleSlipBuyWeightAmt || 0,
          buyVat: saleSlip.saleSlipBuySurtaxAmt || 0,
          buyTotal: saleSlip.saleSlipBuyTotalAmt || 0,
          transportProfitAmt: saleSlip.saleSlipProfitAmt || 0,
          transportProfitRate: saleSlip.saleSlipProfitRate || 0,
          consignBizNumber: "",
          consignContractDate: "",
          consignContractAmt: saleSlip.saleSlipBuyTotalAmt || 0,
          freightNetUse: "",
          saleSlipCodeForTieBreak: saleSlip.saleSlipCode || "",
          itemName: "",
          driverName: "",
          driverTel: "",
          saleSlipWriteId1: saleSlip.saleSlipWriteId1 || "",
          saleSlipWriteId2: saleSlip.saleSlipWriteId2 || "",
          saleSlipWriteDate1: saleSlip.saleSlipWriteDate1 || "",
          saleSlipWriteDate2: saleSlip.saleSlipWriteDate2 || "",
          saleSlipShapeCode: saleSlip.saleSlipShapeCode || "",
          saleSlipUnitCode: saleSlip.saleSlipUnitCode || "",
          saleSlipCompanyCode: saleSlip.saleSlipCompanyCode || "",
          saleSlipCompanyGb: saleSlip.saleSlipCompanyGb || "",
          saleSlipCustCode: saleSlip.saleSlipCustCode || "",
          saleSlipItemCode: saleSlip.saleSlipItemCode || "",
          saleSlipCarCode: saleSlip.saleSlipCarCode || "",
          saleSlipDriverCode: saleSlip.saleSlipDriverCode || "",
          saleSlipOrderSlipCode: saleSlip.saleSlipOrderSlipCode || "",
          saleSlipRemark: saleSlip.saleSlipRemark || "",
          saleSlipReturnGb: saleSlip.saleSlipReturnGb || false,
        };
        setPerformanceModalData(reportData);
        setIsPerformanceModalOpen(true);
      } else {
        toast.error("매출 전표를 찾을 수 없습니다.");
      }
    } catch (error) {
      console.error("매출정보 조회 실패:", error);
      toast.error("매출 전표를 찾을 수 없습니다.");
    } finally {
      setIsLoadingSaleSlip(false);
    }
  };
  const [dateBtnValue, setDateBtnValue] = useState("today");
  const [currentPage, setCurrentPage] = useState(0);
  const [allData, setAllData] = useState<any[]>([]); // 누적된 데이터
  const [tableVer, setTableVer] = useState(0); // 테이블 버전 (리렌더링용)

  // 주문 전표 리포트 조회 (주문일자/회사는 즉시 반영, 거래처/차량정보는 조회 버튼 클릭 시 반영)
  const { data: orderSlipData, isLoading } = useOrderSlipReport({
    page: currentPage,
    size: 20,
    dateFrom: startDate,
    dateTo: endDate,
    searchDto: {
      ...appliedSearchDto,
      companyCode: searchValue || undefined, // 회사는 즉시 반영
    },
  });

  // Footer 총합 계산용 전체 데이터 조회 (size를 매우 크게 설정하여 모든 데이터 가져오기)
  const { data: totalData } = useOrderSlipReport({
    page: 0,
    size: 999999, // 모든 데이터를 한 번에 가져오기
    dateFrom: startDate,
    dateTo: endDate,
    searchDto: {
      ...appliedSearchDto,
      companyCode: searchValue || undefined, // 회사는 즉시 반영
    },
  });

  const totalElements = orderSlipData?.totalElements || 0;
  const totalPages = orderSlipData?.totalPages || 0;

  // 조회 조건이 변경되면 페이지 초기화
  useEffect(() => {
    setCurrentPage(0);
  }, [startDate, endDate, searchValue, appliedSearchDto]);

  // 데이터 누적 로직 (ItemCode.tsx와 동일)
  useEffect(() => {
    if (orderSlipData && orderSlipData.content) {
      if (currentPage === 0) {
        // 첫 페이지면 데이터 초기화 (빈 배열 포함)
        setAllData(orderSlipData.content);
      } else if (orderSlipData.content.length > 0) {
        // 다음 페이지면 기존 데이터에 추가
        setAllData(prev => [...prev, ...orderSlipData.content]);
      }
    }
  }, [orderSlipData, currentPage]);

  // 주문 유형에 따라 데이터 필터링 (실시간)
  const filteredData = useMemo(() => {
    if (selectedValue === "all") {
      return allData; // 전체
    } else if (selectedValue === "option1") {
      return allData.filter(item => item.saleProcessed === true); // 출고 (매출처리됨)
    } else if (selectedValue === "option2") {
      return allData.filter(item => !item.saleProcessed); // 미출고 (매출처리 안됨)
    }
    return allData;
  }, [allData, selectedValue]);

  // 주문 유형 필터가 변경될 때만 테이블 버전 업데이트 (테이블 리렌더링)
  // 무한 스크롤로 데이터가 추가될 때는 스크롤 위치 유지를 위해 업데이트하지 않음
  useEffect(() => {
    setTableVer(prev => prev + 1);
  }, [selectedValue]);

  const hasMore = useMemo(() => {
    if (!orderSlipData) return false;
    return !orderSlipData.last && allData.length < totalElements;
  }, [orderSlipData, allData.length, totalElements]);

  // 무한 스크롤 설정
  const infiniteScrollPage = useInfiniteScrollPage({
    data: filteredData, // 필터링된 데이터 사용
    initialLoadCount: 20,
    loadMoreCount: 20,
    hasMore: hasMore,
    onDataLoad: async () => {
      if (hasMore && !isLoading) {
        setCurrentPage(prev => prev + 1);
      }
    },
  });

  const orderList = filteredData; // 필터링된 데이터를 orderList로 사용

  const columns: TableColumn[] = [
    {
      key: "saleProcessed",
      header: "매출",
      sticky: "left",
      render: (value: boolean) =>
        value ? <img src="/icons/checkGreen.svg" alt="매출 처리됨" className="w-6 h-6 mx-auto" /> : null,
    },
    { key: "orderDate", header: "주문일자", sticky: "left" },
    {
      key: "arrivalTime",
      header: "도착시간",
      sticky: "left",
      render: (value: string) => {
        if (!value) return "";
        return value.replace("T", " ").replace(/\.\d{3}Z?$/, "");
      },
    },
    { key: "carNumber", header: "차량번호", bodyAlign: "left", group: "차량정보" },
    { key: "driverName", header: "기사명", group: "차량정보" },
    { key: "driverTel", header: "전화번호", group: "차량정보" },
    {
      key: "dispatched",
      header: "배차",
      render: (value: boolean) =>
        value ? <img src="/icons/checkGreen.svg" alt="배차 처리됨" className="w-6 h-6 mx-auto" /> : null,
    },
    {
      key: "appExposeYn",
      header: "APP 노출",
      render: (value: string) =>
        value === "Y" ? <img src="/icons/checkGray.svg" alt="APP 노출" className="w-6 h-6 mx-auto" /> : null,
    },
    { key: "itemName", header: "품목명", bodyAlign: "left" },
    { key: "custName", header: "거래처", bodyAlign: "left" },
    { key: "orderQty", header: "주문수량", bodyAlign: "right" },
    { key: "shapeName", header: "화물형태", bodyAlign: "left" },
    { key: "unitName", header: "화물단위", bodyAlign: "left" },

    { key: "inBranch", header: "대리점", bodyAlign: "left", group: "상차정보" },
    { key: "inLocation", header: "상차지", bodyAlign: "left", group: "상차정보" },
    { key: "inQty", header: "수량", bodyAlign: "right", group: "상차정보" },

    { key: "outLocation1", header: "하차지1", bodyAlign: "left", group: "하차정보" },
    { key: "outArea1", header: "지역1", bodyAlign: "left", group: "하차정보" },
    { key: "outQty1", header: "수량1", bodyAlign: "right", group: "하차정보" },
    { key: "outLocation2", header: "하차지2", bodyAlign: "left", group: "하차정보" },
    { key: "outArea2", header: "지역2", bodyAlign: "left", group: "하차정보" },
    { key: "outQty2", header: "수량2", bodyAlign: "right", group: "하차정보" },
    { key: "outLocation3", header: "하차지3", bodyAlign: "left", group: "하차정보" },
    { key: "outArea3", header: "지역3", bodyAlign: "left", group: "하차정보" },
    { key: "outQty3", header: "수량3", bodyAlign: "right", group: "하차정보" },

    { key: "dispatchStaffCode", header: "배차사원" },
    { key: "remark", header: "비고", bodyAlign: "left" },
  ];

  // API 데이터를 SelectBox 옵션으로 변환
  const companyOptions = useMemo(() => {
    return companyCodeList.map((item: any) => ({
      value: item.code || item.codeId?.toString() || "",
      label: item.name || "",
    }));
  }, [companyCodeList]);

  // 검색 필드 옵션
  const searchFieldOptions = [
    { label: "거래처", value: "custName" },
    { label: "차량번호", value: "carNumber" },
    { label: "기사명", value: "driverName" },
    { label: "전화번호", value: "driverTel" },
    { label: "상차지", value: "inLocation" },
    { label: "하차지", value: "outLocation" },
    { label: "하차지역1", value: "outArea1" },
    { label: "하차지역2", value: "outArea2" },
    { label: "배차사원", value: "dispatchStaffName" },
    { label: "품목명", value: "itemName" },
  ];

  const handleReset = () => {
    const resetDate = getDateOffset(1);

    // 검색 조건 초기화
    setSearchValue("");
    setSearchField("custName");
    setSearchKeyword("");
    setSearchField2("carNumber");
    setSearchKeyword2("");
    setExactMatch(false);
    setActualExactMatch(false);
    setAppliedSearchDto({ delYn: "N" });
    setStartDate(resetDate);
    setEndDate(resetDate);
    setSelectedValue("all");
    setSelectedValue2("all2");
    setDateBtnValue("");

    // 선택 초기화
    setSelectedRows([]);

    // 쿼리 무효화하여 데이터 새로 조회 (useEffect에서 currentPage와 allData 초기화됨)
    queryClient.invalidateQueries({
      queryKey: [orderSlipQueryKey],
    });
  };

  const handleSearch = () => {
    // 선택된 필드에 맞게 검색 조건 구성
    const newSearchDto: typeof appliedSearchDto = {
      delYn: "N",
      exactMatch: exactMatch,
    };

    // 검색어가 있으면 선택된 필드에 매핑
    if (searchKeyword.trim()) {
      (newSearchDto as Record<string, string | boolean | undefined>)[searchField] = searchKeyword.trim();
    }
    // 두 번째 검색어가 있으면 선택된 필드에 매핑
    if (searchKeyword2.trim()) {
      (newSearchDto as Record<string, string | boolean | undefined>)[searchField2] = searchKeyword2.trim();
    }

    // 검색 조건 적용
    setActualExactMatch(exactMatch);
    setAppliedSearchDto(newSearchDto);
    // 페이지 초기화
    setAllData([]);
    setCurrentPage(0);
    setTableVer(v => v + 1);
  };

  // 삭제 핸들러
  const handleDelete = () => {
    if (selectedRows.length === 0) {
      toast.warning("삭제할 항목을 선택해주세요.");
      return;
    }

    // 선택된 행들의 데이터 가져오기
    const selectedItems = selectedRows.map(index => orderList[index]).filter(item => item && item.orderSlipCode);

    // 매출이 처리된 항목이 있는지 확인
    const itemsWithSale = selectedItems.filter(item => item.saleProcessed === true);
    if (itemsWithSale.length > 0) {
      toast.error(`매출이 처리된 항목(${itemsWithSale.length}개)은 삭제할 수 없습니다.`);
      return;
    }

    // 선택된 행들의 orderSlipCode 수집
    const orderSlipCodesToDelete = selectedItems.map(item => item.orderSlipCode) as string[];

    if (orderSlipCodesToDelete.length === 0) {
      toast.error("삭제할 수 있는 항목이 없습니다.");
      return;
    }

    // 확인 대화상자
    if (window.confirm(`선택한 ${orderSlipCodesToDelete.length}개의 주문 전표를 삭제하시겠습니까?`)) {
      const delId = userInfo?.name || "";
      if (!delId) {
        toast.error("사용자 정보를 가져올 수 없습니다.");
        return;
      }

      deleteOrderSlipsMutation.mutate(
        { orderSlipCodes: orderSlipCodesToDelete, delId },
        {
          onSuccess: () => {
            toast.success(`${orderSlipCodesToDelete.length}개의 주문 전표가 삭제되었습니다.`);
            handleReset();
          },
          onError: (error: any) => {
            console.error("삭제 실패:", error);
            toast.error("주문 전표 삭제에 실패했습니다.");
          },
        }
      );
    }
  };

  // Excel 업로드 핸들러
  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const writeId = userInfo?.name || "";
    if (!writeId) {
      toast.error("사용자 정보를 가져올 수 없습니다.");
      return;
    }

    uploadExcelMutation.mutate(
      { file, writeId },
      {
        onSuccess: data => {
          // 오류 파일이 있으면 다운로드
          if (data.errorFileBase64 && data.errorFileName) {
            const byteCharacters = atob(data.errorFileBase64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = data.errorFileName;
            link.click();
            window.URL.revokeObjectURL(url);

            toast.error(data.message + " 오류 파일이 다운로드되었습니다.");
          } else if (data.success) {
            toast.success(data.message);
            // 테이블 데이터 새로고침
            queryClient.invalidateQueries({
              queryKey: [orderSlipQueryKey],
            });
          } else {
            toast.error(data.message);
          }
        },
        onError: (error: any) => {
          console.error("Excel 업로드 실패:", error);
          toast.error(error?.response?.data?.message || "Excel 업로드에 실패했습니다.");
        },
      }
    );

    // 같은 파일 재선택 가능하도록 초기화
    event.target.value = "";
  };


  const getContextMenuItems = (row: any, index: number): ContextMenuItem[] => {
    return [
      {
        label: "주문정보",
        onClick: () => {
          // 해당 행을 edit 모드로 모달 열기
          setModalMode("edit");
          setSelectedRowData(row);
          setIsModalOpen(true);
        },
      },
      {
        label: "주문복사",
        onClick: () => {
          // 오늘 날짜로 주문일자 설정하고 상차지/하차지 비우기
          const today = getToday();
          const copiedData = {
            ...row,
            orderDate: today, // 오늘 날짜로 설정
            arrivalTime: undefined, // 도착시간 제거
            // 상차지 정보 비우기
            inLocation: undefined,
            inBranch: undefined,
            inQty: undefined,
            // 하차지 정보 비우기
            outLocation1: undefined,
            outLocation2: undefined,
            outLocation3: undefined,
            outArea1: undefined,
            outArea2: undefined,
            outArea3: undefined,
            outQty1: undefined,
            outQty2: undefined,
            outQty3: undefined,
            // orderSlipCode 제거 (새로 생성되도록)
            orderSlipCode: undefined,
          };
          setModalMode("add");
          setSelectedRowData(copiedData);
          setIsModalOpen(true);
        },
      },
      { divider: true },
      {
        label: "매출정보",
        disabled: !row.saleProcessed || isLoadingSaleSlip, // 매출처리가 되지 않거나 로딩 중이면 disabled
        onClick: () => {
          // orderSlipCode로 매출 전표 조회
          if (!row.orderSlipCode) {
            toast.error("주문 전표 코드가 없습니다.");
            return;
          }

          // 직접 API 호출로 매출정보 조회 (캐싱 문제 완전 해결)
          fetchSaleSlipByOrderCode(row.orderSlipCode);
        },
      },
      {
        label: "매출처리",
        disabled: row.saleProcessed === true, // 매출처리가 완료되면 disabled
        onClick: () => {
          // OrderSlipReportRow를 ReportData로 변환 (PerformanceModal에서 사용 가능한 모든 필드 매핑)
          // 주문일보의 지정 금액을 매출액으로 직접 사용 (매출일보와 동일한 방식)
          const saleAmount = row.salePrice || 0; // 지정 금액 = 매출액
          const saleVat = Math.round(saleAmount * 0.1); // 부가세 = 매출액 × 10%
          const saleTotal = saleAmount + saleVat; // 매출합계 = 매출액 + 부가세

          const reportData: ReportData = {
            // 기본 정보
            reportType: "1", // 계약+운송 (기본값)
            contractUniqueNo: row.orderSlipCode || "",

            // 운송의뢰자
            custName: row.custName || "",
            custBizNumber: "", // 주문 전표에는 없음

            // 계약정보
            contractDate: row.orderDate ? `${row.orderDate}T00:00:00` : getLocalISOString(),
            contractAmount: saleTotal, // 계약금액 = 매출합계
            itemGroupCode: "", // 기본값
            otherTransportUse: "", // 기본값

            // 상차지/하차지 정보 (PerformanceModal에서 사용)
            pickupBranch: row.inBranch || "",
            pickupLocation: row.inLocation || "",
            pickupQty: row.inQty || 0,
            dropoffLocation: row.outLocation1 || "",
            dropoffArea1: row.outArea1 || "",
            dropoffArea2: row.outArea2 || "",
            dropoffArea3: row.outArea3 || "",
            dropoffQty1: row.outQty1 || 0,
            dropoffQty2: row.outQty2 || 0,
            dropoffQty3: row.outQty3 || 0,

            // 배차정보 (PerformanceModal에서 사용)
            vehicleNumber: row.carNumber || "",
            transportDoneDate: row.orderDate ? `${row.orderDate}T00:00:00` : getLocalISOString(),
            transportFee: 0, // 기본값

            // 매출정보 (지정 금액을 매출액으로 직접 사용)
            saleUnitPrice: 0,
            saleAmount: saleAmount, // 지정 금액 = 매출액
            saleMoveFee: 0,
            saleVat: saleVat, // 부가세 = 매출금액 × 10%
            saleTotal: saleTotal, // 매출합계 = 매출금액 + 부가세

            // 매입정보 (기본값 - 사용자가 입력)
            buyUnitPrice: 0,
            buyAmount: 0,
            buyMoveFee: 0,
            buyLaborFee: 0,
            buyMultiFee: 0,
            buyWeightFee: 0,
            buyVat: 0,
            buyTotal: 0,

            // 운송마진 (주문일보 등록 시 입력한 매입% 기준)
            transportProfitAmt: 0, // 자동 계산됨
            transportProfitRate: row.pricePercentage || 0, // 주문일보의 매입%가 운송마진 비율로 들어감

            // 위탁 계약정보 (기본값)
            consignBizNumber: "",
            consignContractDate: "",
            consignContractAmt: 0,
            freightNetUse: "",

            // 기타 (PerformanceModal에서 사용하는 필드들)
            itemName: row.itemName || "",
            driverName: row.driverName || "",
            driverTel: row.driverTel || "",
            saleSlipShapeCode: row.shapeCode || "",
            saleSlipUnitCode: row.unitCode || "",
            saleSlipCompanyCode: row.companyCode || "",
            saleSlipCompanyGb: "1", // 기본값: 계약+운송
            saleSlipCustCode: row.custCode || "",
            saleSlipItemCode: row.itemCode || "",
            saleSlipCarCode: row.carCode || "",
            saleSlipDriverCode: row.driverCode || "",
            saleSlipOrderSlipCode: row.orderSlipCode || "", // 주문 전표 코드
            saleSlipRemark: row.remark || "",
            saleSlipReturnGb: true, // 기본값: Y
          };

          setPerformanceModalData(reportData);
          setIsPerformanceModalOpen(true);
        },
      },
      { divider: true },
      {
        label: "배차요청",
        disabled: row.dispatched === true || !row.carNumber || !row.driverCode, // 배차처리 완료 또는 배차정보 미입력시 disabled
        onClick: () => {
          // OrderSlipReportRow를 DriverOrder로 변환
          // 백엔드 엔티티 필드명(camelCase)에 맞춰서 변환
          // 현재 시간을 년월일 시분까지 형식으로 생성
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, "0");
          const day = String(now.getDate()).padStart(2, "0");
          const hours = String(now.getHours()).padStart(2, "0");
          const minutes = String(now.getMinutes()).padStart(2, "0");
          const currentDateTime = `${year}-${month}-${day}T${hours}:${minutes}:00`;

          const driverOrder: any = {
            orderSlipCode: row.orderSlipCode,
            dateCarSet: currentDateTime,
            itemName: row.itemName,
            inDate: row.orderDate ? `${row.orderDate}T00:00:00` : undefined,
            outDate: row.arrivalTime ? row.arrivalTime.split("T")[0] + "T00:00:00" : undefined,
            timeHour: row.orderSlipTimeHour,
            inBranch: row.inBranch,
            inLocation: row.inLocation,
            qty1: row.inQty,
            outLocation: row.outLocation1,
            outLocation2: row.outLocation2 || undefined,
            outLocation3: row.outLocation3 || undefined,
            outArea1: row.outArea1,
            outDate1: row.arrivalTime ? row.arrivalTime : undefined,
            outTime1: row.orderSlipTimeMin,
            outQty1: row.outQty1,
            outQty1R: undefined,
            outArea2: row.outArea2,
            outDate2: undefined,
            outTime2: undefined,
            outQty2: row.outQty2,
            outQty2R: undefined,
            outArea3: row.outArea3,
            outDate3: undefined,
            outTime3: undefined,
            outQty3: row.outQty3,
            outQty3R: undefined,
            remark: row.remark,
            driverCode: row.driverCode,
            // state는 백엔드에서 "0"으로 설정됨
          };

          insertDriverOrderMutation.mutate(driverOrder, {
            onSuccess: async () => {
              toast.success("배차요청이 완료되었습니다.");

              // 기사에게 푸시 알림 전송
              if (row.driverCode) {
                try {
                  await api.post("/push-notification/new-order", {
                    driverCode: row.driverCode,
                    orderSlipCode: row.orderSlipCode,
                    loadingPlace: row.inLocation || "",
                    unloadingPlace: row.outLocation1 || "",
                  });
                  console.log("[배차요청] 푸시 알림 전송 완료");
                } catch (pushError) {
                  console.warn("[배차요청] 푸시 알림 전송 실패:", pushError);
                  // 푸시 실패해도 배차요청은 성공했으므로 에러 표시 안함
                }
              }

              // 배차요청 후 테이블 새로고침을 위해 handleReset 호출
              handleReset();
            },
            onError: (error: any) => {
              console.error("배차요청 실패:", error);
              toast.error(error?.response?.data?.message || "배차요청에 실패했습니다.");
            },
          });
        },
      },
      {
        label: "배차취소",
        disabled: row.dispatched !== true, // 배차처리가 안되어 있으면 disabled
        onClick: () => {
          if (!row.orderSlipCode) {
            toast.error("주문 전표 코드가 없습니다.");
            return;
          }

          // 확인 대화상자
          if (window.confirm("배차를 취소하시겠습니까?")) {
            // 배차취소 전에 기사 코드 저장 (푸시 알림용)
            const driverCodeForPush = row.driverCode;
            const orderSlipCodeForPush = row.orderSlipCode;
            const loadingPlaceForPush = row.inLocation || "";
            const unloadingPlaceForPush = row.outLocation1 || "";

            deleteDriverOrderMutation.mutate(row.orderSlipCode, {
              onSuccess: async () => {
                toast.success("배차취소가 완료되었습니다.");

                // 기사에게 배차취소 푸시 알림 전송
                if (driverCodeForPush) {
                  try {
                    await api.post("/push-notification/cancel-order", {
                      driverCode: driverCodeForPush,
                      orderSlipCode: orderSlipCodeForPush,
                      loadingPlace: loadingPlaceForPush,
                      unloadingPlace: unloadingPlaceForPush,
                    });
                    console.log("[배차취소] 푸시 알림 전송 완료");
                  } catch (pushError) {
                    console.warn("[배차취소] 푸시 알림 전송 실패:", pushError);
                    // 푸시 실패해도 배차취소는 성공했으므로 에러 표시 안함
                  }
                }

                // 배차취소 후 테이블 새로고침을 위해 handleReset 호출
                handleReset();
              },
              onError: (error: any) => {
                console.error("배차취소 실패:", error);
                toast.error(error?.response?.data?.message || "배차취소에 실패했습니다.");
              },
            });
          }
        },
      },
      { divider: true },
      {
        label: "기사정보",
        onClick: () => {
          // 기사 코드가 있으면 해당 기사로 필터링하여 모달 열기
          // driverCode가 있으면 코드로, 없으면 driverName으로 검색
          const searchValue = row.driverName || "";
          setSelectedDriverCode(searchValue);
          setIsDriverSearchModalOpen(true);
        },
      },
    ];
  };

  // Footer 그리드 데이터 계산 (전체 데이터 기준)
  const footerGrid: (FooterCell | null)[][] = useMemo(() => {
    // 전체 데이터가 없으면 빈 값 반환
    if (!totalData?.content || totalData.content.length === 0) {
      return [
        [
          { label: "주문수량", value: "0.00" },
          { label: "상차수량", value: "0.00" },
          { label: "하차수량1", value: "0.00" },
          { label: "하차수량2", value: "0.00" },
          { label: "하차수량3", value: "0.00" },
        ],
      ];
    }

    // 주문 유형 필터 적용 (전체 데이터에도 필터 적용)
    let filteredTotalData = totalData.content;
    if (selectedValue === "option1") {
      filteredTotalData = totalData.content.filter(item => item.saleProcessed === true);
    } else if (selectedValue === "option2") {
      filteredTotalData = totalData.content.filter(item => !item.saleProcessed);
    }

    // 전체 데이터 기준으로 합산
    // 주문수량은 각 로우의 orderQty만 사용 (orderQty2, orderQty3는 없음)
    const totalOrderQty = filteredTotalData.reduce((sum, item) => sum + (item.orderQty || 0), 0);

    const totalInQty = filteredTotalData.reduce((sum, item) => sum + (item.inQty || 0), 0);

    const totalOutQty1 = filteredTotalData.reduce((sum, item) => sum + (item.outQty1 || 0), 0);
    const totalOutQty2 = filteredTotalData.reduce((sum, item) => sum + (item.outQty2 || 0), 0);
    const totalOutQty3 = filteredTotalData.reduce((sum, item) => sum + (item.outQty3 || 0), 0);

    return [
      [
        { label: "주문수량", value: totalOrderQty.toFixed(2) },
        { label: "상차수량", value: totalInQty.toFixed(2) },
        { label: "하차수량1", value: totalOutQty1.toFixed(2) },
        { label: "하차수량2", value: totalOutQty2.toFixed(2) },
        { label: "하차수량3", value: totalOutQty3.toFixed(2) },
      ],
    ];
  }, [totalData, selectedValue]);

  return (
    <div className="flex flex-col h-full">
      <SearchBoxLayout>
        <div className="flex items-end mb-3">
          <SelectBox
            label="회사"
            className="w-50"
            value={searchValue}
            onChange={e => setSearchValue(e)}
            options={companyOptions}
          />
          <div className="mr-3" />
          <SelectBox
            label={searchFieldOptions.find(opt => opt.value === searchField)?.label || "검색필드"}
            className="min-w-24"
            value={searchField}
            onChange={e => setSearchField(e)}
            options={searchFieldOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-50"
            value={searchKeyword}
            onChange={e => setSearchKeyword(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <div className="mr-3" />
          <SelectBox
            label={searchFieldOptions.find(opt => opt.value === searchField2)?.label || "검색필드"}
            className="min-w-24"
            value={searchField2}
            onChange={e => setSearchField2(e)}
            options={searchFieldOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-50"
            value={searchKeyword2}
            onChange={e => setSearchKeyword2(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <div className="mr-3" />
          <DateBox
            label="주문일자"
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <div className="mr-2" />
          <DateSearchBtn
            mode="range"
            value={dateBtnValue}
            onChangeValue={setDateBtnValue}
            currentRange={{ startDate, endDate }}
            onDateSelect={({ startDate, endDate }) => {
              setStartDate(startDate);
              setEndDate(endDate);
            }}
          />
        </div>
        <div className="flex items-end">
          <ButtonGroup
            label="주문유형"
            options={[
              { value: "all", label: "전체" },
              { value: "option1", label: "출고" },
              { value: "option2", label: "미출고" },
            ]}
            value={selectedValue}
            onChange={setSelectedValue}
          />
          <div className="mr-3" />
          {/* <ButtonGroup
            label=""
            options={[
              { value: "all2", label: "전체" },
              { value: "option3", label: "화주WEB 주문" },
            ]}
            value={selectedValue2}
            onChange={setSelectedValue2}
          /> */}
          <div className="flex gap-[6px] ml-auto items-end">
            <CheckBox
              label="일치"
              labelPosition="right"
              checked={exactMatch}
              onChange={e => setExactMatch(e.target.checked)}
            />
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
            <Button variant="search" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <CountBox count={orderSlipData?.totalElements || 0} />
          <div className="flex gap-2">
            <PermissionButton
              permission="canCreate"
              variant="primary"
              onClick={() => {
                if (selectedRows.length === 0) {
                  toast.error("배차요청할 항목을 선택해주세요.");
                  return;
                }

                // 선택된 항목들 검증
                const selectedData = selectedRows.map(index => orderList[index]).filter(Boolean);

                // 이미 배차완료된 항목 체크
                const alreadyDispatched = selectedData.filter(row => row.dispatched === true);
                if (alreadyDispatched.length > 0) {
                  toast.error(`이미 배차완료된 항목이 ${alreadyDispatched.length}건 있습니다.`);
                  return;
                }

                // 차량정보 미입력 항목 체크
                const noCarInfo = selectedData.filter(row => !row.carNumber || !row.driverCode);
                if (noCarInfo.length > 0) {
                  toast.error(`차량/기사 정보가 없는 항목이 ${noCarInfo.length}건 있습니다.`);
                  return;
                }

                // 현재 시간 생성
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, "0");
                const day = String(now.getDate()).padStart(2, "0");
                const hours = String(now.getHours()).padStart(2, "0");
                const minutes = String(now.getMinutes()).padStart(2, "0");
                const currentDateTime = `${year}-${month}-${day}T${hours}:${minutes}:00`;

                // 배차요청 데이터 생성
                const driverOrders: any[] = selectedData.map(row => ({
                  orderSlipCode: row.orderSlipCode,
                  dateCarSet: currentDateTime,
                  itemName: row.itemName,
                  inDate: row.orderDate ? `${row.orderDate}T00:00:00` : undefined,
                  outDate: row.arrivalTime ? row.arrivalTime.split("T")[0] + "T00:00:00" : undefined,
                  timeHour: row.orderSlipTimeHour,
                  inBranch: row.inBranch,
                  inLocation: row.inLocation,
                  qty1: row.inQty,
                  outLocation: row.outLocation1,
                  outLocation2: row.outLocation2 || undefined,
                  outLocation3: row.outLocation3 || undefined,
                  outArea1: row.outArea1,
                  outDate1: row.arrivalTime ? row.arrivalTime : undefined,
                  outTime1: row.orderSlipTimeMin,
                  outQty1: row.outQty1,
                  outQty1R: undefined,
                  outArea2: row.outArea2,
                  outDate2: undefined,
                  outTime2: undefined,
                  outQty2: row.outQty2,
                  outQty2R: undefined,
                  outArea3: row.outArea3,
                  outDate3: undefined,
                  outTime3: undefined,
                  outQty3: row.outQty3,
                  outQty3R: undefined,
                  remark: row.remark,
                  driverCode: row.driverCode,
                }));

                // 순차적으로 배차요청 처리
                let successCount = 0;
                let failCount = 0;

                const processNext = (index: number) => {
                  if (index >= driverOrders.length) {
                    // 모든 요청 완료
                    if (successCount > 0) {
                      toast.success(`${successCount}건 배차요청 완료`);
                      handleReset();
                    }
                    if (failCount > 0) {
                      toast.error(`${failCount}건 배차요청 실패`);
                    }
                    return;
                  }

                  insertDriverOrderMutation.mutate(driverOrders[index], {
                    onSuccess: () => {
                      successCount++;
                      processNext(index + 1);
                    },
                    onError: () => {
                      failCount++;
                      processNext(index + 1);
                    },
                  });
                };

                processNext(0);
              }}
            >
              배차요청
            </PermissionButton>
            <PermissionButton
              permission="canCreate"
              variant="primary"
              onClick={() => excelFileInputRef.current?.click()}
              disabled={uploadExcelMutation.isPending}
            >
              {uploadExcelMutation.isPending ? "업로드 중..." : "엑셀 등록"}
            </PermissionButton>
            <Button variant="white" onClick={downloadOrderSlipUploadTemplate}>
              양식
            </Button>
            <input
              ref={excelFileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleExcelUpload}
              className="hidden"
            />
            <PermissionButton
              permission="canCreate"
              variant="etc"
              onClick={() => {
                setModalMode("add");
                setSelectedRowData(null);
                setIsModalOpen(true);
              }}
            >
              추가
            </PermissionButton>
            <PermissionButton
              permission="canUpdate"
              variant="etc"
              onClick={() => {
                if (selectedRows.length === 1) {
                  const selectedIndex = selectedRows[0];
                  const selectedData = orderList[selectedIndex];
                  if (selectedData) {
                    setModalMode("edit");
                    setSelectedRowData(selectedData);
                    setIsModalOpen(true);
                  }
                } else {
                  toast.warning("수정할 항목을 하나만 선택해주세요.");
                }
              }}
            >
              수정
            </PermissionButton>
            <PermissionButton permission="canDelete" variant="etc" onClick={handleDelete}>
              삭제
            </PermissionButton>
            <PrintButton data={orderList} columns={columns} title="수주관리" />
            <ExcelButton data={orderList} columns={columns} fileName="수주관리" />
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <Table
            key={`order-table-v${tableVer}`}
            {...infiniteScrollPage.tableProps}
            resizable
            className="h-full"
            columns={columns}
            stickyCheckbox
            stickyRowNumber
            showCheckbox={true}
            showRowNumber={true}
            selectedRows={selectedRows}
            onRowSelect={setSelectedRows}
            onRowDoubleClick={(row) => {
              setModalMode("edit");
              setSelectedRowData(row);
              setIsModalOpen(true);
            }}
            contextMenuItems={getContextMenuItems}
            maxHeight="100%"
          />
        </div>
      </main>

      <footer className="flex-shrink-0 p-4 bg-gray-50 border-t">
        <Footer grid={footerGrid} />
      </footer>
      <Add
        isOpen={isModalOpen}
        mode={modalMode}
        initialData={selectedRowData}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRowData(null);
        }}
        onSave={data => {
          if (modalMode === "add") {
            setIsModalOpen(false);
            setSelectedRowData(null);
            // 주문복사 또는 추가 후 테이블 새로고침
            handleReset();
            queryClient.invalidateQueries({
              queryKey: [orderSlipQueryKey, "report"],
              refetchType: "active", // 또는 "all"
            });
          } else {
            // 연속 저장일 경우 모달 유지
            // 일반 저장일 경우 모달 닫기
            setIsModalOpen(false);
            setSelectedRowData(null);
            handleReset();
            queryClient.invalidateQueries({
              queryKey: [orderSlipQueryKey, "report"],
              refetchType: "active", // 또는 "all"
            });
          }
        }}
      />
      <PerformanceModal
        isOpen={isPerformanceModalOpen}
        onClose={() => {
          setIsPerformanceModalOpen(false);
          setPerformanceModalData(null);
        }}
        onSave={() => {
          // 매출처리 저장 후 테이블 새로고침
          handleReset();
        }}
        mode={performanceModalData?.saleSlipCodeForTieBreak ? "edit" : "add"}
        initialData={performanceModalData || undefined}
      />
      <DriverSearchModal
        isOpen={isDriverSearchModalOpen}
        onClose={() => {
          setIsDriverSearchModalOpen(false);
          setSelectedDriverCode(null);
        }}
        onSelect={(driver: DriverCode) => {
          // 기사 선택 시 처리 (필요시 추가 로직 구현)
          setIsDriverSearchModalOpen(false);
          setSelectedDriverCode(null);
        }}
        initialSearchValue={selectedDriverCode || undefined}
      />
    </div>
  );
}
